import { cva, type VariantProps } from 'class-variance-authority'
import { animate, MotionConfig, motion, useReducedMotion } from 'motion/react'
import { type ReactNode, useEffect, useId, useRef, useState } from 'react'

import { cn } from '../lib/utils'

// Ported from beUI's "Switch" (https://beui.dev/components/motion/switch), a shadcn-style
// copy-paste component (there is no npm package — the source is meant to be copied in). The
// motion — the heavy, high-mass thumb spring, the press squish, and the disabled shake — is
// kept as authored there. What we changed: colors are swapped for Parsley design-system
// tokens, and the flat component grew two variant axes (brand `variant` + `surface` fit) via
// cva. beUI notes its components are recreated from existing work across the web and ships
// them to be copied; credited here per that intent.
//
// This is a NEW component, not a replacement for `switch.tsx` (the Radix boolean switch still
// in production use). It shares the `checked` / `onCheckedChange` API so a call site can swap.

// Heavy, deliberate thumb — high mass keeps the travel weighty without wobble (beUI's tuning).
const THUMB_SPRING = { type: 'spring', stiffness: 800, damping: 80, mass: 4 } as const

// The thumb slides by exactly one of its own widths: travel = trackContent − thumb, and every
// `size` below is dimensioned so trackWidth === 2·thumb + 2·padding, which makes travel === the
// thumb width. So `x: '100%'` (a percentage of the thumb's own size) is the exact distance at
// EVERY size, and stays correct under the ui package's rem-scaling where a hardcoded px value
// would not. This replaces beUI's Framer `layout` animation on purpose: `layout` interpolates
// against the page-projection coordinate space, so an ancestor that reflows mid-slide (a status
// label widening, a count changing) folds its positional delta — vertical component included —
// into the thumb's tween and bends the puck diagonally. A local transform stays inside the
// track, so the whole toggle just rides the shift and the puck slides straight across.
// INVARIANT: any new `size` must keep trackWidth === 2·thumb + 2·padding or the puck won't rest
// centred at the ends. default: 48 === 2·20 + 2·4; sm: 36 === 2·16 + 2·2.
const THUMB_ON_X = '100%'

// The track's fill is state-dependent: the checked colour comes from `variant`, the unchecked
// colour from `surface` (so the off track reads correctly on whatever it sits on). cva can't
// see runtime state, so `state` is an internal variant driven by the `checked` prop and paired
// with `variant`/`surface` via compoundVariants. It is not part of the public API.
const toggleTrackVariants = cva(
  cn(
    'group peer inline-flex shrink-0 cursor-pointer items-center rounded-full outline-none transition-colors duration-200',
    // Explicit pelorous, matching Button / PillButton / SegmentedToggle / TopbarButton and the
    // rest of the DS. This used to be `ring-ring`, which is the stock shadcn token — still amber
    // (hue 38) because it was never re-themed to Parsley's palette, so the toggle was the one
    // control that focused a different colour from everything beside it. Width goes 2px -> 3px
    // for the same reason. (`--ring` itself is left alone; input.tsx and dialog.tsx still read it.)
    'focus-visible:ring-[3px] focus-visible:ring-pelorous-500 dark:focus-visible:ring-pelorous-300 focus-visible:ring-offset-2 focus-visible:ring-offset-background',
    'disabled:cursor-not-allowed disabled:opacity-60'
  ),
  {
    variants: {
      // Track dims per size — see the THUMB_ON_X invariant. Thumb dims live in toggleThumbVariants.
      size: { default: 'h-7 w-12 px-1', sm: 'h-5 w-9 px-0.5' },
      variant: { primary: '', secondary: '' },
      surface: { card: '', sunken: '', 'on-image': '' },
      // `state` keys only the checked/unchecked fill via compoundVariants below — the thumb's
      // position is a local transform on the slider, not flex justification.
      state: { checked: '', unchecked: '' }
    },
    compoundVariants: [
      // Checked (on) — brand fill from `variant`.
      { variant: 'primary', state: 'checked', class: 'bg-primary' },
      { variant: 'secondary', state: 'checked', class: 'bg-brand-green' },
      // Unchecked (off) — neutral track tuned to the surface it rests on. Cards are near-white,
      // so a soft track reads; the sunken cream surface needs a touch more weight to separate;
      // on a photo/colour hero the neutral greys vanish, so use a translucent white track.
      { surface: 'card', state: 'unchecked', class: 'bg-muted-foreground/35' },
      { surface: 'sunken', state: 'unchecked', class: 'bg-muted-foreground/50' },
      { surface: 'on-image', state: 'unchecked', class: 'bg-white/25' }
    ],
    defaultVariants: { size: 'default', variant: 'primary', surface: 'card', state: 'unchecked' }
  }
)

// Thumb dims pair with the track `size` (kept as its own cva so the size prop drives both). The
// surface hairline rings are applied at the call site since they don't vary by size.
const toggleThumbVariants = cva('block rounded-full bg-card shadow-md dark:bg-foreground', {
  variants: {
    size: { default: 'h-5 w-5', sm: 'h-4 w-4' }
  },
  defaultVariants: { size: 'default' }
})

export interface ToggleProps extends Pick<VariantProps<typeof toggleTrackVariants>, 'variant' | 'surface' | 'size'> {
  checked: boolean
  onCheckedChange: (checked: boolean) => void
  disabled?: boolean
  /** Optional trailing label — clicking it toggles the switch (wired via `htmlFor`). */
  label?: ReactNode
  /** Accessible name when there's no visible `label` (e.g. an icon-only or hero toggle). */
  'aria-label'?: string
  /** Override the generated id (needed to wire an external `<label htmlFor>`). */
  id?: string
  className?: string
}

/**
 * Boolean toggle with a weighty spring thumb — the puck settles with mass rather than snapping,
 * squishes toward its destination on press, and gives a small shake when a disabled switch is
 * poked (`prefers-reduced-motion` makes everything instant). `variant` picks the on-state brand
 * fill (`primary` amber / `secondary` brand green); `surface` tunes the off-state track for the
 * ground it sits on (`card` white / `sunken` tinted / `on-image` photo or colour hero); `size`
 * is `default` or `sm` (a denser control for list rows). Controlled only — pass `checked` +
 * `onCheckedChange`. Renders a real `role="switch"` button; pass `label` or `aria-label` for its
 * accessible name.
 */
export function Toggle({
  checked,
  onCheckedChange,
  disabled,
  label,
  id,
  variant,
  surface,
  size,
  className,
  'aria-label': ariaLabel
}: ToggleProps) {
  const generatedId = useId()
  const switchId = id ?? generatedId
  const thumbRef = useRef<HTMLDivElement>(null)
  const reduce = useReducedMotion()
  const [isPressed, setIsPressed] = useState(false)
  const [isPointer, setIsPointer] = useState(false)

  // Disabled shake feedback when pressed.
  useEffect(() => {
    if (!thumbRef.current || reduce) return
    if (disabled && isPressed) {
      animate(thumbRef.current, { x: [0, -2, 2, -1, 0] }, { delay: 0.2, duration: 0.6 })
    }
  }, [disabled, isPressed, reduce])

  const squish = !disabled && isPointer && isPressed && !reduce

  return (
    <MotionConfig transition={reduce ? { duration: 0 } : THUMB_SPRING}>
      <span className={cn('inline-flex items-center gap-3', className)}>
        <motion.button
          id={switchId}
          type="button"
          role="switch"
          aria-checked={checked}
          aria-label={ariaLabel}
          disabled={disabled}
          onClick={() => !disabled && onCheckedChange(!checked)}
          onPointerDown={(e) => {
            setIsPressed(true)
            setIsPointer(e.type.startsWith('pointer'))
          }}
          onPointerUp={() => setIsPressed(false)}
          onPointerLeave={() => setIsPressed(false)}
          initial={false}
          data-state={checked ? 'checked' : 'unchecked'}
          className={toggleTrackVariants({ size, variant, surface, state: checked ? 'checked' : 'unchecked' })}>
          {/* Outer slider owns the on/off position (a local x transform); the inner puck owns
              the press squish and the disabled shake — split across two elements so the shake's
              x keyframes never fight the position transform. */}
          <motion.div aria-hidden initial={false} animate={{ x: checked ? THUMB_ON_X : '0%' }} className="pointer-events-none">
            <motion.div
              ref={thumbRef}
              animate={{ scale: squish ? 0.9 : 1 }}
              className={cn(
                toggleThumbVariants({ size }),
                // The white puck can wash into the tinted sunken track — a hairline keeps its edge.
                surface === 'sunken' && 'ring-1 ring-black/5 dark:ring-white/10',
                // On a photo/colour hero the puck needs a brighter hairline to hold its edge.
                surface === 'on-image' && 'ring-1 ring-white/30'
              )}
            />
          </motion.div>
        </motion.button>
        {label ? (
          <label htmlFor={switchId} className="cursor-pointer text-sm text-foreground">
            {label}
          </label>
        ) : null}
      </span>
    </MotionConfig>
  )
}

export { toggleTrackVariants }
